// Follow-ups API client & preset helpers (WP-04).
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { stripUndefined } from './callable';

export type FollowupPreset = '1h' | '2h' | '4h' | '6h' | '12h' | '24h' | '1d' | '2d' | '1w';

export interface FollowupDoc {
  id: string;
  tenantId: string;
  conversationId: string;
  subscriberId?: string;
  dueAt: number;
  reason: string;
  note?: string;
  status: 'open' | 'done' | 'cancelled';
  createdBy: string;
  createdAt: number;
  completedBy?: string;
  completedAt?: number;
  remindedAt?: number;
  updatedAt: number;
}

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

export const saveFollowup = call<
  {
    id?: string;
    conversationId: string;
    dueAt?: number;
    preset?: FollowupPreset;
    reason: string;
    note?: string;
  },
  { ok: boolean; id: string }
>('followupSave');

export const completeFollowup = call<
  { id: string; status?: 'done' | 'cancelled' },
  { ok: boolean }
>('followupComplete');

export const deleteFollowup = call<{ id: string }, { ok: boolean }>('followupDelete');

export const listFollowups = call<
  { conversationId?: string; status?: 'open' | 'done' | 'cancelled'; limit?: number },
  { ok: boolean; items: FollowupDoc[] }
>('followupList');

export const exportFollowups = call<
  { status?: 'open' | 'done' | 'cancelled' },
  { ok: boolean; url: string }
>('followupExport');

/** Calculates future timestamp for quick presets */
export function calcPresetDueAt(preset: FollowupPreset, now: number = Date.now()): number {
  const hour = 3600 * 1000;
  const day = 24 * hour;
  switch (preset) {
    case '1h': return now + 1 * hour;
    case '2h': return now + 2 * hour;
    case '4h': return now + 4 * hour;
    case '6h': return now + 6 * hour;
    case '12h': return now + 12 * hour;
    case '24h': return now + 24 * hour;
    case '1d': return now + 1 * day;
    case '2d': return now + 2 * day;
    case '1w': return now + 7 * day;
  }
}
